import { 
  Component, Input, Output, EventEmitter, forwardRef, 
  HostListener, ElementRef, ViewChild, TemplateRef, ContentChild, 
  OnDestroy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import { fromEvent, Subscription } from 'rxjs';

import { CommonModule } from '@angular/common';
export interface MultiSelectItem {
  id: string;
  text: string;
}

@Component({
    selector: 'app-multi-select-control',
    templateUrl: './multi-select-control.component.html',
    styleUrls: ['./multi-select-control.component.css'],
    animations: [
        trigger('dropdownAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-10px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
            ])
        ])
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSelectDropdownComponent),
            multi: true
        }
    ],
    imports: [CommonModule, FormsModule]
})
export class MultiSelectDropdownComponent implements ControlValueAccessor, OnDestroy  {
  @Input() items: MultiSelectItem[] = [];
  @Input() selectedItems: string[] = [];
  @Input() placeholder = 'Select options';
  @Input() disabled = false;
  @Input() showSearch = true;
  @Input() selectAllText = 'Select All';
  @Input() showSelectAll: boolean = true;

  @Output() selectionChange = new EventEmitter<string[]>();
  @Output() dropdownOpen = new EventEmitter<boolean>();
  
  @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ContentChild('itemTemplate') itemTemplate?: TemplateRef<any>;
  @ContentChild('chipTemplate') chipTemplate?: TemplateRef<any>;
  
  isOpen = false;
  searchQuery = '';
  focusedItemIndex = -1;

   private clickOutsideSubscription?: Subscription;


 hostElement: HTMLElement;

  constructor(private elementRef: ElementRef) {
   this.hostElement = this.elementRef.nativeElement;
  }
  
  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  /**
   * Gets an item by its ID
   */
  getItemById(itemId: string): MultiSelectItem | undefined {
    return this.items.find(item => item.id === itemId);
  }

  // Add this method to your component
private adjustDropdownPosition(): void {
  if (!this.dropdownMenu) return;
  
  const dropdownElement = this.dropdownMenu.nativeElement;
  const rect = dropdownElement.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  
  // If dropdown is going off screen at the bottom
  if (rect.bottom > viewportHeight) {
    dropdownElement.style.maxHeight = `${viewportHeight - rect.top - 20}px`;
  }
}

 private handleClickOutside(): void {
  
    // Remove any existing subscription
    this.clickOutsideSubscription?.unsubscribe();

    if (this.isOpen) {
      this.clickOutsideSubscription = fromEvent(document, 'click').subscribe((event: Event) => {
        const target = event.target as HTMLElement;
        if (!this.elementRef.nativeElement.contains(target)) {
          this.closeDropdown();
        }
      });
    }
  }

  // Update toggleDropdown method
  toggleDropdown(): void {
    if (this.disabled) return;
    
    this.isOpen = !this.isOpen;
    this.dropdownOpen.emit(this.isOpen);
    
    if (this.isOpen) {
      setTimeout(() => {
        if (this.showSearch) {
          this.searchInput.nativeElement.focus();
        }
        this.adjustDropdownPosition();
        this.handleClickOutside(); // Add this line
      }, 0);
    } else {
      this.clickOutsideSubscription?.unsubscribe();
    }
  }

  // Add this new method
  closeDropdown(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.dropdownOpen.emit(false);
      this.clickOutsideSubscription?.unsubscribe();
      this.onTouched();
    }
  }

  // Add ngOnDestroy
  ngOnDestroy(): void {
    this.clickOutsideSubscription?.unsubscribe();
  }

  /**
   * Filters items based on search query
   */
  get filteredItems(): MultiSelectItem[] {
    if (!this.searchQuery) return this.items;
    return this.items.filter(item => 
      item.text.toLowerCase().includes(this.searchQuery.toLowerCase())
    );
  }

  /**
   * Checks if an item is selected
   */
  isSelected(itemId: string): boolean {
    return this.selectedItems.includes(itemId);
  }

  /**
   * Toggles item selection
   */
  toggleItemSelection(itemId: string): void {

    
    
    if (this.disabled) return;
    
    const index = this.selectedItems.indexOf(itemId);
    if (index === -1) {
      this.selectedItems = [...this.selectedItems, itemId];
    } else {
      this.selectedItems = this.selectedItems.filter(id => id !== itemId);
    }
    
    this.emitChanges();
  }

  /**
   * Toggles select all items
   */
  toggleSelectAll(): void {
    if (this.disabled) return;
    
    if (this.selectedItems.length === this.filteredItems.length) {
      this.selectedItems = [];
    } else {
      this.selectedItems = this.filteredItems.map(item => item.id);
    }
    
    this.emitChanges();
  }

  /**
   * Removes a selected item
   */
  removeSelectedItem(itemId: string, event: Event): void {
    event.stopPropagation();
    this.selectedItems = this.selectedItems.filter(id => id !== itemId);
    this.emitChanges();
  }

  /**
   * Clears all selected items
   */
  clearAll(): void {
    this.selectedItems = [];
    this.emitChanges();
  }

  /**
   * Handles keyboard navigation
   */
  @HostListener('keydown', ['$event'])
  handleKeyboardEvents(event: KeyboardEvent): void {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(-1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.focusedItemIndex >= 0 && this.focusedItemIndex < this.filteredItems.length) {
          this.toggleItemSelection(this.filteredItems[this.focusedItemIndex].id);
        }
        break;
      case 'Escape':
        this.isOpen = false;
        this.dropdownOpen.emit(false);
        break;
      case 'Tab':
        this.isOpen = false;
        this.dropdownOpen.emit(false);
        break;
    }
  }

  /**
   * Moves focus up or down in the dropdown list
   */
  private moveFocus(direction: number): void {
    const newIndex = this.focusedItemIndex + direction;
    
    if (newIndex >= 0 && newIndex < this.filteredItems.length) {
      this.focusedItemIndex = newIndex;
      this.scrollIntoView(this.focusedItemIndex);
    }
  }

  /**
   * Scrolls the focused item into view
   */
  private scrollIntoView(index: number): void {
    const dropdownElement = this.dropdownMenu.nativeElement;
    const itemElements = dropdownElement.querySelectorAll('.dropdown-item');
    
    if (itemElements[index]) {
      itemElements[index].scrollIntoView({
        block: 'nearest'
      });
    }
  }

  /**
   * Emits changes to parent components
   */
  private emitChanges(): void {
    this.onChange(this.selectedItems);
    this.onTouched();
    this.selectionChange.emit(this.selectedItems);
  }

  // ControlValueAccessor implementation
  writeValue(value: string[]): void {
    this.selectedItems = value || [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}